// src/ExamQuestions8.jsx
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from '../css/modules/ExamQuestions.module.css';

const ExamQuestions8 = () => {
  const navigate = useNavigate();

  const handleNext = () => {
    navigate('/issf-przepisy-egzamin-pozwolenie-na-bron');
  };

  return (
    <div className={styles.contactContainer}>
      <Helmet>
        <title>Patent strzelecki i pozwolenie na broń – ucz się z najlepszych pytań</title>
        <meta
          name="description"
          content="Zestaw pytań egzaminacyjnych obejmujący konkurencje ISSF, postawy strzeleckie, odległości i przepisy karne związane z bronią. Sprawdź swoją wiedzę przed egzaminem!"
        />
      </Helmet>

      <div className={styles.content}>
        <h1 className="mainHeader">Patent strzelecki i egzamin na broń? Ucz się sprytniej, nie ciężej</h1>

        <div className="intro">
          Przygotowanie do <strong>egzaminu na patent strzelecki</strong> to nie tylko znajomość obsługi broni i zasad bezpieczeństwa, ale również opanowanie przepisów dotyczących zawodów strzeleckich oraz znajomość podstaw prawa karnego w kontekście broni palnej.

          📘 W tej części omawiamy najczęstsze pytania z zakresu konkurencji sportowych według standardów ISSF, rodzajów postaw, odległości w strzelaniach oraz możliwych sankcji za złamanie przepisów. Te informacje są niezbędne, by pozytywnie zdać <strong>egzamin na broń</strong> i otrzymać <strong>pozwolenie na broń</strong>.

          🔗 Jeśli dopiero zaczynasz, sprawdź:
          <ul>
            <li><a href="/">Jak wygląda egzamin na broń – przewodnik</a></li>
            <li><a href="/pytania-na-pozwolenie-na-bron-patent-strzeleckii">Kompendium o pozwoleniu na broń i ustawie</a></li>
          </ul>

          🎮 Zdobądź przewagę przed egzaminem! Odwiedź <a href="https://pistolet.org" target="_blank" rel="noopener noreferrer">pistolet.org</a> i rozwiąż nasze testy z wyjaśnieniami. Połączenie wiedzy i zabawy sprawi, że <strong>egzamin na patent strzelecki</strong> będzie czystą formalnością.
        </div>

        <h2>🎯 Konkurencje strzeleckie i postawy</h2>
        <ul className="reasonList">
          <li className={styles.customAnswer}>
            <h3>111. 🟠 Jakie konkurencje należą do strzelby gładkolufowej?</h3>
            <p>
              Do konkurencji z użyciem strzelby gładkolufowej zaliczamy:
              <strong> skeet, trap, double trap oraz automatic trap</strong>. Wszystkie polegają na strzelaniu do rzutków, czyli ceramicznych dysków, wyrzucanych z określonych punktów pod różnymi kątami. Strzelec musi wykazać się refleksem i precyzją w warunkach zmiennej dynamiki.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>112. 📥 Kiedy można załadować magazynek podczas zawodów na 25 m?</h3>
            <p>
              Magazynek można załadować dopiero po wydaniu komendy „<strong>ŁADUJ</strong>” przez prowadzącego strzelanie. Załadowanie przed tą komendą jest poważnym naruszeniem regulaminu i może skutkować dyskwalifikacją.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>113. 📏 Jakie są odległości w konkurencjach pistoletowych ISSF?</h3>
            <p>
              Standardowe odległości to <strong>10 m, 25 m i 50 m</strong>. Dobór dystansu zależy od rodzaju konkurencji (np. pistolet pneumatyczny – 10 m, sportowy – 25 m, dowolny – 50 m).
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>114. 📐 Jakie są odległości w konkurencjach karabinowych ISSF?</h3>
            <p>
              W konkurencjach karabinowych wyróżnia się trzy odległości: <strong>10 m, 50 m i 300 m</strong>. Zawody na 300 m odbywają się głównie na poziomie międzynarodowym i wymagają szczególnej precyzji.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>115. 🎯 Jakie odległości obowiązują w konkurencjach ISSF?</h3>
            <p>
              Oficjalne odległości to: <strong>10, 25, 50 oraz 300 metrów</strong>. Obowiązują w zależności od konkurencji, rodzaju broni i kategorii zawodnika (junior/senior).
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>116. 🎯 Na jaką odległość strzela się w konkurencji pistolet sportowy 30+30?</h3>
            <p>
              Konkurencja 30+30 rozgrywana jest na <strong>25 metrów</strong>. Zawodnik oddaje 60 strzałów – 30 w tempie wolnym, 30 w szybkim.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>117. 🧠 Jak wygląda konkurencja pistolet szybkostrzelny?</h3>
            <p>
              W tej konkurencji strzela się do <strong>pięciu tarcz</strong> z odległości <strong>25 metrów</strong>. Czas reakcji i rytm oddawania strzałów są tu kluczowe – to sprawdzian refleksu i precyzji.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>118. 💨 Jakie konkurencje odbywają się na 10 metrów?</h3>
            <p>
              Karabin i pistolet pneumatyczny – zawodnik strzela z pozycji <strong>stojącej</strong>. To najczęstsza konkurencja wśród juniorów i początkujących.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>119. 🎯 Jakie konkurencje odbywają się na 25 metrów?</h3>
            <p>
              Konkurencje z pistoletem sportowym, centralnego zapłonu i szybkostrzelne – wszystkie wymagają opanowania precyzji i tempa.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>120. 🎯 Jakie konkurencje odbywają się na 50 metrów?</h3>
            <p>
              Karabin dowolny, karabin sportowy i konkurencje z tarczą ruchomą. To konkurencje zaawansowane, wymagające szczególnego przygotowania.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>121. 🧍 Jakie są postawy w konkurencjach karabinowych?</h3>
            <p>
              <strong>Leżąca</strong> – największa stabilność, <strong>stojąca</strong> – najtrudniejsza, <strong>klęcząca</strong> – kompromis między mobilnością a precyzją. W trójboju zawodnik strzela we wszystkich trzech postawach.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>122. 🏅 Jakie są dyscypliny strzelectwa sportowego?</h3>
            <p>
              Oficjalnie ISSF wyróżnia trzy podstawowe grupy: <strong>karabin, pistolet, strzelba</strong>. Każda z nich zawiera wiele konkurencji o różnych zasadach.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>123. 🔫 Jakie typy pistoletów dopuszcza ISSF?</h3>
            <p>
              Dowolny, sportowy, szybkostrzelny, centralnego zapłonu oraz pneumatyczny – każda konkurencja ma ściśle określone wymagania techniczne.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>124. 🟡 Na czym polega konkurencja SKEET?</h3>
            <p>
              Strzelanie do <strong>rzutków</strong> wyrzucanych z dwóch wież (niskiej i wysokiej), w różnych konfiguracjach. Wymaga szybkiej oceny trajektorii i błyskawicznego oddania strzału.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>125. 🟠 Na czym polega konkurencja TRAP?</h3>
            <p>
              Rzutki wyrzucane są z <strong>bunkra</strong> – w sposób losowy, pod różnymi kątami i wysokościami. Kluczowa jest znajomość rytmu i precyzja w pierwszym ułamku sekundy.
            </p>
          </li>
        </ul>

        <h2>⚖️ Przepisy karne dotyczące broni</h2>
        <ul className="reasonList">
          <li className={styles.customAnswer}>
            <h3>126. 🚨 Jakie kary stosuje się za naruszenie regulaminu ISSF?</h3>
            <p>
              W zależności od naruszenia – od <strong>ustnego ostrzeżenia</strong>, przez <strong>odjęcie punktów</strong>, aż po <strong>dyskwalifikację</strong>. Regulamin jasno określa zasady reakcji na nieprawidłowości.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>127. 🍺 Co grozi za noszenie broni po spożyciu alkoholu lub środków psychoaktywnych?</h3>
            <p>
              Kara <strong>aresztu</strong> lub <strong>grzywny</strong>, a także możliwość cofnięcia pozwolenia na broń. To jedno z najpoważniejszych naruszeń.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>128. ❌ Co grozi za porzucenie broni lub amunicji?</h3>
            <p>
              <strong>Do 2 lat pozbawienia wolności</strong> – zgodnie z Kodeksem karnym. Broń musi być stale nadzorowana, nawet chwilowe porzucenie może skutkować poważnymi konsekwencjami.
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>129. 📋 Co grozi za brak rejestracji lub złożenia broni?</h3>
            <p>
              <strong>Grzywna lub areszt</strong> – każda broń musi być zarejestrowana w odpowiednim terminie (najczęściej 5 dni od nabycia).
            </p>
          </li>

          <li className={styles.customAnswer}>
            <h3>130. 🔫 Co grozi za posiadanie lub zbycie broni pneumatycznej bez rejestracji?</h3>
            <p>
              <strong>Areszt lub grzywna</strong>. Dotyczy również osób nieposiadających pozwolenia na broń, które kupiły broń pneumatyczną powyżej 17 J.
            </p>
          </li>
        </ul>

        <div className="buttonContainer">
          <button onClick={handleNext} className={styles.secondaryButton}>
            👉 Przejdź do kolejnych pytań
          </button>
          <button onClick={() => navigate('/')} className={styles.primaryButton}>
            🧠 Sprawdź testy na patent strzelecki
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExamQuestions8;
